import { ReactNode } from 'react'
import { Badge } from './badge'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './popover'
import { Building2 } from 'lucide-react'

export interface BadgeListItem {
  id: string | number
  name: string
  shortName?: string
  badgeClassName?: string
  [key: string]: any // Permite más propiedades en caso de ser necesario
}

export interface BadgeListPopoverProps {
  items: BadgeListItem[]
  globalAccess?: boolean
  globalLabel?: string
  emptyLabel?: string
  popoverTitle?: string
  icon?: ReactNode
}

export function BadgeListPopover({
  items,
  globalAccess = false,
  globalLabel = 'Acceso total',
  emptyLabel = 'Sin asignar',
  popoverTitle = 'Elementos Asignados',
  icon = <Building2 className="h-4 w-4 text-slate-400 shrink-0" />
}: BadgeListPopoverProps) {
  
  if (globalAccess) {
    return (
      <Badge variant="secondary" className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium">
        {globalLabel}
      </Badge>
    )
  }
  
  if (!items || items.length === 0) {
    return <span className="text-sm text-slate-400 italic">{emptyLabel}</span>
  }

  const firstItem = items[0]
  const remaining = items.slice(1)

  return (
    <div className="flex flex-wrap items-center gap-1.5 min-w-0">
      <Badge variant="secondary" className={`font-medium whitespace-normal break-words text-wrap max-w-full text-left h-auto py-1.5 px-2.5 ${firstItem.badgeClassName || 'bg-slate-100 hover:bg-slate-200 text-slate-700'}`}>
        <span className="block">{firstItem.shortName || firstItem.name}</span>
      </Badge>

      {remaining.length > 0 && (
        <Popover>
          <PopoverTrigger asChild>
            <button className="inline-flex items-center justify-center rounded-md bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-slate-600 hover:bg-slate-200 hover:text-slate-900 transition-colors focus:outline-none focus:ring-2 focus:ring-slate-300 focus:ring-offset-1">
              +{remaining.length}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-max max-w-[320px] p-0 rounded-lg border-slate-200 shadow-md" side="top" align="start">
            <div className="px-3 py-2 border-b border-slate-100 bg-slate-50">
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                {popoverTitle} ({items.length})
              </p>
            </div>
            <div className="max-h-[220px] overflow-y-auto p-1 space-y-0.5 custom-scrollbar">
              {items.map(item => (
                <div key={item.id} className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-md hover:bg-slate-100 transition-colors cursor-default">
                  {icon}
                  <span className="text-sm text-slate-700 font-medium">{item.name}</span>
                </div>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  )
}
